import {Controller} from "@hotwired/stimulus"
import 'ol/ol.css';
import {Map, View} from 'ol';
import {Tile as TileLayer, Vector as VectorLayer} from 'ol/layer';
import {OSM, Vector as VectorSource} from 'ol/source';
import {fromLonLat, transformExtent} from 'ol/proj';
import {Icon, Style} from "ol/style";
import {containsExtent, Extent} from "ol/extent";
import {createSegmentedCircleIcon} from "../../../js/icon-drawer";
import {parseEnvelopeWKT, parseWkt} from "../../../js/wkt-utils";
import {DEFAULT_COORDINATES} from "../../../js/constants";
import {Filter, FiltersHandler} from "./filters_handler";
import {Debouncer} from "../../../js/debouncer";
import {filterToURLSearchParams} from "./utils/filter_to_url_search_params";
import {pointToFeature} from "./points/point_to_feature";
import {Point} from "./points/point";
import {Cluster} from "./points/cluster";
import {EventOccurrencesContainerHandler} from "./event_occurrences_container_handler";
import Overlay from 'ol/Overlay';
import markerUrl from '../../../assets/icons/map-marker.svg';
import urgentMarkerUrl from '../../../assets/icons/urgent-map-marker.svg';
import {Announcement} from "./points/announcement";

const markerStyleCache: { [key: string]: Style } = {};

function getMarkerStyleKey(colors: string[], number: number): string {
  return `${colors.sort().join('-')}_${number}`;
}

export default class extends Controller {
  static targets = ["map", "checkbox", "sidebar"]

  declare readonly mapTarget: HTMLElement;
  declare readonly sidebarTarget: HTMLElement;

  private map: Map | null = null;

  private popup: HTMLElement | null = null;
  private popupOverlay: Overlay | null = null;
  private vectorSource: VectorSource | null = null;
  private clusterLayer: VectorLayer | null = null;

  private debouncer: Debouncer<URLSearchParams, (Point | Cluster | Announcement)[]> = new Debouncer<URLSearchParams, (Point | Cluster | Announcement)[]>();
  private filtersHandler: FiltersHandler = new FiltersHandler((filter) => this.onFilterChanged(filter));
  private eventOccurrencesContainerHandler = new EventOccurrencesContainerHandler();

  private announcementIconStyle: Style | null = null;
  private urgentAnnouncementIconStyle: Style | null = null;

  connect() {
    this.filtersHandler.initialize();
    const modalEventOccurrencesContainer = document.getElementById('event-occurrences-modal-container');

    this.eventOccurrencesContainerHandler.initialize(
      this.sidebarTarget,
      modalEventOccurrencesContainer!,
      this.application
    );

    this.announcementIconStyle = new Style({
      image: new Icon({
        src: markerUrl,
        anchor: [0.5, 0.5],
        scale: 0.155
      }),
    });

    this.urgentAnnouncementIconStyle = new Style({
      image: new Icon({
        src: urgentMarkerUrl,
        anchor: [0.5, 0.5],
        scale: 0.2
      }),
      zIndex: 10
    });

    this.initMap();
  }

  initMap(): void {
    const {lng, lat} = DEFAULT_COORDINATES;

    const zoomAttributeValue = this.mapTarget.getAttribute('data-zoom');
    const zoom = zoomAttributeValue ? Number(zoomAttributeValue) : 10;

    const centerAttributeValue = this.mapTarget.getAttribute('data-center');
    const center = centerAttributeValue ? centerAttributeValue.split(',').map(Number) : [lng, lat];

    this.vectorSource = new VectorSource()
    this.clusterLayer = new VectorLayer({
      source: this.vectorSource,
      style: (feature) => {
        const type = feature.get('type');
        if (['cluster', 'point'].includes(type)) {
          const colors = feature.get('colors');
          const eventOccurrencesCount: number = feature.get('event_occurrences_count');

          const key = getMarkerStyleKey(colors, eventOccurrencesCount);
          if (markerStyleCache[key]) {
            return markerStyleCache[key];
          }

          const iconSrc: string = createSegmentedCircleIcon(colors, eventOccurrencesCount);

          const style = new Style({
            image: new Icon({
              src: iconSrc,
              anchor: [0.5, 0.5],
              scale: 1
            }),
          });

          markerStyleCache[key] = style;
          return style;
        } else if ('announcement' === type) {
          return feature.get('urgent') ? this.urgentAnnouncementIconStyle! : this.announcementIconStyle!
        }
      }
    })

    const view = new View({
      center: fromLonLat(center),
      zoom: zoom
    });

    this.map = new Map({
      target: this.mapTarget,
      layers: [
        new TileLayer({
          source: new OSM(),
          className: 'osm-tile-layer'
        }),
        this.clusterLayer
      ],
      view: view
    });

    this.popup = document.getElementById('popup');
    if (this.popup) {
      this.popupOverlay = new Overlay({
        element: this.popup,
        // positioning: 'bottom-center',
        offset: [0, 20], // Adjust this to position below your marker
        autoPan: {
          animation: {
            duration: 250,
          },
        },
      });

      this.map.addOverlay(this.popupOverlay);
    }

    this.map.on('moveend', () => {
      if (!this.map) {
        return;
      }

      const view = this.map.getView();
      const zoom = view.getZoom();
      const center = view.getCenter();
      const extent: Extent = view.calculateExtent(this.map.getSize());

      if (!zoom || !center || !extent) {
        return;
      }

      console.log(`PreviousZoom: ${this.filtersHandler.filter.zoom}, current: ${zoom}`);
      console.log(`PreviousExtent: ${this.filtersHandler.filter.extent}, current: ${extent}`);

      if (this.filtersHandler.filter.zoom && this.filtersHandler.filter.extent) {
        // If the zoom hasn't changed and the current extent is fully contained in the previous extent...
        if (zoom === this.filtersHandler.filter.zoom && containsExtent(this.filtersHandler.filter.extent, extent)) {
          this.filtersHandler.setMapFilter({zoom: zoom, center: center, extent: extent});
          console.log('Using cached features.');
          return; // Skip requesting new features.
        }
      }

      this.filtersHandler.setMapFilter({zoom: zoom, center: center, extent: extent});
      this.onFilterChanged(this.filtersHandler.filter).catch(console.error);
    });

    this.map.on('singleclick', (evt) => {
      this.popupOverlay?.setPosition(undefined);

      const map = this.map;
      if (!map) {
        return;
      }

      const view = map.getView();
      if (!view) {
        return;
      }

      const features = this.map?.getFeaturesAtPixel(evt.pixel);
      if (features?.length) {
        const feature = features[0];

        const type = feature.get('type');
        if (type === 'cluster') {
          const envelopeWKT = feature.get('envelopeWkt');
          const extent4326 = parseEnvelopeWKT(envelopeWKT);

          if (extent4326) {
            // Transform the envelope extent from EPSG:4326 to EPSG:3857.
            const extent3857 = transformExtent(extent4326, 'EPSG:4326', 'EPSG:3857');

            // Calculate the resolution needed to fit the extent.
            const mapSize = map.getSize();
            const resolutionForExtent = view.getResolutionForExtent(extent3857, mapSize);
            // Calculate the corresponding zoom level.
            const fitZoom = view.getZoomForResolution(resolutionForExtent) || 13;

            if (fitZoom < 13) {
              view.fit(extent3857, {
                duration: 500,
                padding: [20, 20, 20, 20]
              });
              return;
            }
          }

          const clusterCenterWKT = feature.get('center_wkt');
          const clusterCenter = parseWkt(clusterCenterWKT);
          if (clusterCenter) {
            view.animate({center: clusterCenter.getCoordinates(), zoom: 13})
          }
        } else if (type === 'point') {
          const eventOccurrenceIds: number[] = feature.get('event_occurrence_ids');

          let idsParams = new URLSearchParams({});
          eventOccurrenceIds?.map(eventOccurrenceId => {
            idsParams.append('ids[]', eventOccurrenceId.toString());
          });

          fetch(`event_occurrences.json?${idsParams.toString()}`)
            .then(response => response.json())
            .then(this.eventOccurrencesContainerHandler.show)
            .catch(error => console.error('Error fetching records:', error));
        } else if (type === 'announcement') {
          if (this.popup) {
            const urgent = feature.get('urgent');
            const advImage = feature.get('advertising_thumb_url');

            this.popup.innerHTML = `
              <img src="${urgent ? urgentMarkerUrl : markerUrl}" style="width: 32px; height: 32px; position: absolute; left: -17px; bottom: -9px" alt="marker" />
              <a href="/public/announcements/${feature.get('id')}" data-turbo="false">
                ${advImage ? `
                  <div class="card-header p-0 mx-0 mt-0 position-relative">
                    <img class="img-fluid border-radius-lg header-image" src="${advImage}" alt="announement advertising image">
                  </div>
                ` : ''}
                <div class="card-body p-3 d-flex flex-column">
                  <div class="title mb-1">
                    ${feature.get('title')}
                  </div>
                  <div class="ol-popup-content">
                    ${feature.get('text')}
                  </div>
                  <div class="price">
                      ${feature.get('price')}
                  </div>
                </div>
              </a>`
          }

          const geometry = feature.getGeometry();
          if (geometry) {
            this.popupOverlay?.setPosition((geometry as any).getCoordinates());
          }

          const closer = this.popup?.querySelector('.popup-closer') as HTMLElement;
          if (closer) {
            closer.onclick = () => {
              this.popupOverlay?.setPosition(undefined);
              return false;
            };
          }
        }
      }
    });
  }

  async onFilterChanged(filter: Filter) {
    const urlSearchParams = filterToURLSearchParams(filter);
    window.history.replaceState({}, "", `map?${urlSearchParams.toString()}`);

    try {
      const points = await this.debouncer.debounce(this.loadPoints, urlSearchParams);

      this.vectorSource?.clear()
      const features = points.map(pointToFeature);
      this.vectorSource?.addFeatures(features)
    } catch (error) {
      console.warn(error)
    }
  }

  async loadPoints(params: URLSearchParams): Promise<(Point | Cluster | Announcement)[]> {
    let requestUrl = `map/points.json?${params.toString()}`;
    const response = await fetch(requestUrl);
    return response.json();
  }

  closeEvents() {
    this.sidebarTarget.classList.remove('active');
    const sidebarEventOccurrencesContainer = this.sidebarTarget.querySelector('#sidebar-event-occurrences-container');
    if (sidebarEventOccurrencesContainer) sidebarEventOccurrencesContainer.innerHTML = '';
  }

  disconnect() {
    this.map?.dispose();
    this.debouncer.cancelPending();
  }
}